import { useState } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router-dom";

/**
 * Navigation state that asks /ai-search for a fresh session even when the
 * navigation itself is a `replace` — React Router turns a link to the URL
 * already in the address bar into one (e.g. "Home" clicked mid-"thinking",
 * while the URL is still a bare /ai-search).
 */
export const NEW_AI_SEARCH_SESSION_STATE = { newAiSearchSession: true } as const;

function requestsNewSession(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Partial<typeof NEW_AI_SEARCH_SESSION_STATE>).newAiSearchSession === true
  );
}

/**
 * Remount key for the AI search session. Every push/pop onto /ai-search
 * (header "Home", header search, My Conversations, browser back/forward)
 * yields a new key, so the page starts from a clean slate for that URL. The
 * page's own in-place URL rewrites (`chatId`, `results`, `q` — all `replace`)
 * keep the current key, so an active conversation isn't torn down by them.
 */
export function useAiSearchSessionKey(): string {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [sessionKey, setSessionKey] = useState(location.key);

  const isNewSession =
    navigationType !== NavigationType.Replace || requestsNewSession(location.state);
  if (isNewSession && location.key !== sessionKey) {
    setSessionKey(location.key);
  }

  return sessionKey;
}
